
import { Vue, Prop, Component, Watch } from "vue-property-decorator";
import Papa from "papaparse";
import draggable from "vuedraggable";
import RightClickMenu from "@/components/widgets/RightClickMenu.vue";
import ColumnVisibilityModal from "@/components/widgets/Modals/ColumnVisibilityModal.vue";
import ManageOrderFiltersModal from "@/components/widgets/Modals/ManageOrderFiltersModal.vue";
import { getModule } from "vuex-module-decorators";
import { AccountModule, FacilityModule, OrderModule } from "@/store/modules";
import { DISPATCH_ORDER_SETTINGS } from "@/consts";
import OrderPreviewModal from "@/components/widgets/Modals/OrderPreviewModal.vue";
import _ from "lodash";

const orderModule = getModule(OrderModule);
const accountModule = getModule(AccountModule);
const facilityModule = getModule(FacilityModule);

@Component({
  components: {
    RightClickMenu,
    ColumnVisibilityModal,
    draggable,
    ManageOrderFiltersModal,
    OrderPreviewModal
  }
})
export default class DispatchOrdersTable extends Vue {
  rightClickLocation: Object = { x: 0, y: 0 };
  showHelp: boolean = false;
  draggingData: boolean = false;
  showManageFiltersModal: boolean = false;
  hasMoved: boolean = false;
  filterFields: Array<any> = [];
  adhocFilters: any = null;
  availablePageSizes: Array<any> = [
    {
      value: 30,
      text: "30"
    },
    {
      value: 50,
      text: "50"
    },
    {
      value: 70,
      text: "70"
    }
  ];

  @Prop({ default: "table" })
  public id: string;

  @Prop({ default: true })
  public showFilters: boolean;

  @Prop({ default: "" })
  public containerClass: string;

  @Prop({ default: null })
  public tableMenuItem: Array<any>;

  @Prop({ default: "table" })
  public helpType: string;

  @Prop({ default: null })
  public tableSettings: Array<any>;

  @Prop({ default: false })
  public showFieldFilter: boolean;

  @Prop({ default: () => [] })
  public availableFilters: Array<any>;

  @Watch("filterFields")
  filtersChanged(newValue) {
    let columnNames = newValue.filter(x => x !== undefined).map(x => x.key)
    accountModule.updateColumnPreferences({ tableName: "dispatchTable", columns: columnNames });
  }

  @Watch("showManageFiltersModal")
  showFilterToggled(val, oldVal) {
    if (val !== oldVal) this.loadFilters();
  }

  @Watch("selectedFilter")
  filterSelectChanged(val, oldVal) {
    if (val !== oldVal) this.loadFilters();
  }

  loadFilters() {
    let activeFilter = accountModule.getAdhocFilterIfActive;
    if (!activeFilter) activeFilter = accountModule.getSelectedSavedOrderSearch;
    if (activeFilter) {
      this.adhocFilters = _.cloneDeep(activeFilter.searchFields);
    } else {
      this.adhocFilters = [];
    }
  }

  checkHeader(value) {
  if (value.draggedContext.element.disableMoving || value.related.innerText === '' || value.dragged.innerText === '') {
     return false;
  }
  return true;
}

  created() {
    const dispatchPrefs = this.dispatchTableColumnPreferences;
    this.loadFilters();
    accountModule.setSelectedSavedOrderSearch(this.selectedFilter);
    this.filterFields = dispatchPrefs.map(x => this.availableFields.filter(y => y.key === x)[0]).sort((a, b) => b.sort || 0 - a.sort || 0);
  }

  get unmovingFields() {
    return this.filterFields.filter(x => x.disableMoving);
  }

  get availableFields() {
    return orderModule.getAvailableOrderFields;
  }

  get loading() {
    return orderModule.loading;
  }

  get orderFields() {
    return orderModule.orderFields;
  }

  get tableData() {
    return orderModule.data;
  }

  get filters() {
    return accountModule.orderSearches;
  }

  get totalItems() {
    if (orderModule.pageData) {
      const items = orderModule.pageData.totalItems;
      return items ? items : 0;
    } else {
      return 0;
    }
  }

  get currentSortAscending() {
    return orderModule.dataSortAscending;
  }
  get currentSort() {
    return orderModule.dataSortKey;
  }

  get dispatchTableColumnPreferences() {
    return accountModule.dispatchTableColumnPreferences;
  }

  get pageSize() {
    return orderModule.pageData.pageSize;
  }

  get defaultSavedSearch() {
    return (
      accountModule.defaultSavedSearchesUserSettings &&
      accountModule.defaultSavedSearchesUserSettings[0]
    );
  }

  get defaultSearchSubscription() {
    return (
      accountModule.defaultSearchSubscriptionsUserSettings &&
      accountModule.defaultSearchSubscriptionsUserSettings[0]
    );
  }

  get selectedFilter() {
    if (accountModule.lastOrderSearch || accountModule.lastSearchSubscription) {
      return accountModule.lastOrderSearch?.value || Number(accountModule.lastSearchSubscription?.value);
    } else if (accountModule.adhocFilter) {
      return -1;
    } else if (Number(this.defaultSavedSearch) || Number(this.defaultSearchSubscription)) {
      return (
        Number(this.defaultSavedSearch) ||
        (Number(this.defaultSearchSubscription) && (parseInt(this.defaultSearchSubscription) + 1) * -1)
      );
    } else {
      return this.selectedOrderFilterId;
    }
  }

  set selectedFilter(newValue) {
    const lastSearch = this.savedOrderSearchList.find(item => item.value === newValue);
    if (newValue > 0) {
      accountModule.setLastOrderSearch(lastSearch);
      accountModule.setLastSearchSubscription(null);
    } else {
      accountModule.setLastSearchSubscription(lastSearch);
      accountModule.setLastOrderSearch(null);
    }
    accountModule.setSelectedSavedOrderSearch(newValue);
  }

  get selectedOrders() {
    return orderModule.selectedOrders;
  }

  get selectedOrderCount() {
    return orderModule.selectedOrderCount;
  }

  get userOrderFields() {
    return orderModule.getUserOrderFields;
  }

  get availableOrderFields() {
    return orderModule.getAvailableOrderFields;
  }

  get savedOrderSearchList() {
    return accountModule.getSavedOrderSearchList;
  }

  get dispatchOrderSettings() {
    return DISPATCH_ORDER_SETTINGS;
  }

  get currentPageSize(): number {
    return orderModule.pageData.pageSize;
  }

  set currentPageSize(val: number) {
    orderModule.setPageSize(val);
  }

  get selectedOrderFilterId() {
    return accountModule.selectedSavedOrderSearchId;
  }

  set selectedOrderFilterId(newValue) {
    accountModule.setSelectedSavedOrderSearch(newValue);
  }

  get localData() {
    return JSON.parse(JSON.stringify(this.tableData));
  }

  get getSubscribedFacilitySavedOrderSearchList() {
    return facilityModule.getSubscribedFacilitySavedOrderSearches;
  }

  set localData(newVal) {
    this.localData = newVal;
  }

  get sortedData() {
    return this.localData;
  }

  set sortedData(data) {
    //Only the truck data allow to drop an order to add order to truck. The order table can't drag and drop
    if (this.draggingData) {
      let truckIdx = 0;
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          const truck = data[key];
          if (truck.number > -1) {
            truckIdx = parseInt(key);
            break;
          }
        }
      }
      this.$emit("menuAction", "addOrdersToTruck", data[truckIdx]);
    }
    this.draggingData = false;
  }

  handleToggleField(key, isVisible) {
    if (!isVisible) {
      const existingIndex = this.filterFields.findIndex(x => x.key === key);
      if (existingIndex < 0) return;
      this.filterFields.splice(existingIndex, 1);
    } else {
      const existing = orderModule.getAvailableOrderFields.find(x => x.key === key);
      if (existing) this.filterFields = [...this.filterFields, existing].sort((a, b) => b.sort || 0 - a.sort || 0);
    }
  }

  resetFilterFields() {
    this.filterFields = _.cloneDeep(orderModule.getAvailableOrderFields);
  }

  unselectAll() {
    orderModule.unselectAll();
  }

  putFeature() {
    if (this.id === "truckTable") {
      return this.putTable;
    } else {
      return false;
    }
  }

  putTable(item, index, event) {
    this.draggingData = true;
  }

  async sort(s) {
    await this.$emit("sort", s);
  }

  onRightClicked(rowData, event) {
    event.preventDefault();

    this.rightClickLocation = { x: event.x, y: event.y };

    if (!rowData.selected) {
      rowData.selected = true;
      this.updateRowSelectionStatus(rowData);
    }

    if( this.selectedOrders!=null ) {
      this.tableMenuItem
        .forEach(mi => {
          mi.disabled = false;

          if(mi.requiresPermission != null) {
            this.selectedOrders.forEach(x => {
              let orders = this.sortedData.filter(o => o.number == x.number);
              if(orders != null && orders.length>0) {
                let order = orders[0];
                if(!this.hasAgentPermission(mi.requiresPermission, order.deliveryForId)) {
                  mi.disabled = true;
                }
              }
            });
          }
        });
      }

    this.tableMenuItem
    .filter(x => x.title == 'Add to Truck')
      .forEach(x => x.disabled = orderModule.selectedOrders.some(x => x.orderType == 'WILL'));
  }

  onDoubleClicked(orderData) {
    let orderNumber = orderData.number;
    this.$router.push(`/order/${orderNumber}`);
  }

  onRowClick(rowData, event) {
    this.clearRightClick();
    if (event.ctrlKey) {
      window.open("/order/" + rowData.number, "_blank");
    } else {
      if (rowData.selected) {
        rowData.selected = false;
      } else {
        rowData.selected = true;
      }
      this.updateRowSelectionStatus(rowData);
    }
  }

  updateRowSelectionStatus(row) {
    orderModule.updateRowSelectionStatus(row);
  }

  clearRightClick() {
    this.rightClickLocation = null;
    this.draggingData = false;
  }

  downloadCSV(e) {
    e.preventDefault();
    let blob = new Blob([Papa.unparse(this.localData)], {
      type: "text/csv;charset=utf-8;"
    });

    let link = document.createElement("a");

    let url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "table.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  clickMenu(item) {
    this.$store.dispatch(item.action, { selectedItems: this.selectedOrders });
    this.clearRightClick();
  }

  onScroll({ target: { scrollTop, clientHeight, scrollHeight } }) {
    if (
      Math.ceil(scrollTop) + clientHeight >= scrollHeight &&
      !this.loading &&
      this.totalItems > orderModule.data.length
    ) {
      this.$emit("loadMoreData");
    }
  }

  getValue(object, path) {
    if (!path) return;
    return path.split(".").reduce((r, k) => (r ? r[k] : ""), object);
  }
}
